import ExcelJS from 'exceljs';

/**
 * This function checks if the station to be added passes requirement for allowed characters and format
 * The allowable characters are only alphabets and spaces
 * If a non-allowable character exists, error message key is updated and loop is broken to return isValid as false and the error message key. The key
 * is used to read from the translation object.
 * If the station is valid, an updated station string is created removing extra spaces, and proper casing the string
 * @param station - Takes in input station and checks if naming convention matches with standards
 * @returns Returns isValid and if string is valid, return updatedStation modified to the right format, else if string is
 * not valid, return errorMessageKey string
 */
const checkIsStationValid = (station: string) => {
  let errorKey;
  let updatedStation = '';
  let isValid = station.length > 0 ? true : false;
  let previousCharacter;
  for (let stringIndex = 0; stringIndex < station.length; stringIndex += 1) {
    const character = station[stringIndex];
    // Test if allowable characters only
    if (!/[a-zA-Z\s]/.test(character)) {
      isValid = false;
      errorKey = 'menuItemMapping.inputError.station.invalidCharacter';
      break;
    }

    // Remove extra spaces
    if (!(character === ' ' && (!previousCharacter || previousCharacter === ' '))) {
      // If previous character is a space or is the first letter of the string, uppercase it. Else, lowercase it.
      if (previousCharacter === ' ' || !previousCharacter) {
        updatedStation += character.toUpperCase();
      } else {
        updatedStation += character.toLowerCase();
      }
    }
    previousCharacter = character;
  }

  return [isValid, isValid ? updatedStation : errorKey];
};

/**
 * This function checks if the menuItemName to be added passes requirement for allowed characters and format
 * While this function was initially written in pure regex, there was difficulty in forming one with the below checks, hence a custom function is created
 * This function checks for
 * 1. Valid characters (only alphabets, dashes, parentheses, single quotation and double quotations are allowed)
 * 2. Same symbols cannot appear in succession
 * 3. Must contain at least 1 alphabet
 * 4. Balanced parentheses and double quotation marks
 * If any of the above check fails, error message key is updated and loop is broken to return isValid as false and the error message key. The key
 * is used to read from the translation object.
 * If the menuItemName is valid, an updated menuItemName string is created removing extra spaces, and proper casing the string
 * @param menuItemName - Takes in input menuItemName and checks if naming convention matches with standards
 * @returns Returns isValid and if string is valid, return updatedMenuItemName modified to the right format, else if string is
 * not valid, return errorMessageKey string
 */
const checkIsMenuItemNameValid = (menuItemName: string) => {
  const stackSymbol = [];
  let prevNonSymbolCharacter;
  let updatedMenuItemName = '';
  let isValid = menuItemName.length > 0 ? true : false;
  let errorKey;
  for (let stringIndex = 0; stringIndex < menuItemName.length; stringIndex += 1) {
    const character = menuItemName[stringIndex];
    const prevUpdatedCharacter = updatedMenuItemName.charAt(updatedMenuItemName.length - 1);
    const nextCharacter =
      stringIndex === menuItemName.length - 1 ? null : menuItemName[stringIndex + 1];
    // Test if allowable characters only
    if (!/[a-zA-Z'()"-\s]/.test(character)) {
      isValid = false;
      errorKey = 'menuItemMapping.inputError.menuItemName.invalidCharacter';
      break;
    }

    // Test if last character of updated string is same as current character, if it is a symbol
    if (/['()"-]/.test(character) && updatedMenuItemName && prevUpdatedCharacter === character) {
      isValid = false;
      errorKey = 'menuItemMapping.inputError.menuItemName.consecutiveSameSymbol';
      break;
    }

    // Remove extra spaces
    if (!(character === ' ' && (!prevNonSymbolCharacter || prevNonSymbolCharacter === ' '))) {
      // Push and pop parentheses and double quotation marks from stackSymbol
      if (character === '(' || (character === '"' && stackSymbol[stackSymbol.length - 1] !== '"')) {
        stackSymbol.push(character);
      } else if (
        (character === ')' && stackSymbol[stackSymbol.length - 1] === '(') ||
        (character === '"' && stackSymbol[stackSymbol.length - 1] === '"')
      ) {
        stackSymbol.pop();
      } else if (
        (character === ')' && stackSymbol[stackSymbol.length - 1] !== '(') ||
        (character === '"' && stackSymbol[stackSymbol.length - 1] !== '"')
      ) {
        isValid = false;
        errorKey = errorKey || 'menuItemMapping.inputError.menuItemName.imbalancedSymbol';
        break;
      }

      // The following conditions determine if the current character is to be in uppercase:
      // 1. If the previous character is a space
      // 2. If the previous character does not exist (i.e. start of the string)
      // 3. If the previous character is a symbol, except single quotation
      // 4. If the previous character is a single quotation and (current character is not 's' or if it is 's' the following character
      // should not be a space or end of string)
      // Otherwise, current character is to be in lowercase
      if (
        prevNonSymbolCharacter === ' ' ||
        !prevNonSymbolCharacter ||
        /[()"-]/.test(prevUpdatedCharacter) ||
        (prevUpdatedCharacter === "'" &&
          (character !== 's' ||
            (character === 's' && nextCharacter !== ' ' && nextCharacter !== null)))
      ) {
        updatedMenuItemName += character.toUpperCase();
      } else {
        updatedMenuItemName += character.toLowerCase();
      }

      // Ignore double quotation and open parenthesis for prevNonSymbolCharacter
      if (!/['()"-]/.test(character)) {
        prevNonSymbolCharacter = character;
      }
    }
  }
  // If prevNonSymbolCharacter does not exist, there is no alphabet present
  if (!prevNonSymbolCharacter) {
    isValid = false;
    errorKey = errorKey || 'menuItemMapping.inputError.menuItemName.atLeast1Alphabet';
  }

  // If stackSymbol is not empty, parentheses and double quotations are not balanced
  if (stackSymbol.length !== 0) {
    isValid = false;
    errorKey = errorKey || 'menuItemMapping.inputError.menuItemName.imbalancedSymbol';
  }

  return [isValid, isValid ? updatedMenuItemName : errorKey];
};

/**
 * This function checks if the costPerKilogram to be added is a valid positive number.
 * @param costPerKilogram - Takes in input costPerKilogram and checks if is a valid positive number
 * @returns Returns true if costPerKilogram is a valid positive number, returns false if it is not
 */
const checkIsCostPerKilogramValid = (costPerKilogram: number) => {
  const costPerKilogramInNumberFormat = Number(costPerKilogram);
  return !Number.isNaN(costPerKilogramInNumberFormat) && costPerKilogramInNumberFormat > 0;
};

/**
 * This function is called when user uploads Property Menu Item Mapping Spreadsheet, which traverses through
 * the spreadsheet to extract the menu item and mappings to be created. For any errors, their groupIdString is
 * returned to notify users to correct them before trying again
 * @param event - Change event from input element. Provides spreadsheet file uploaded by client
 * @returns Object.arrArrMenuItemAndMappingToBeCreatedByMenuId - Array of array menuItemAndMappingToBeCreated
 * seperated by menuId
 * @returns Object.arrErrorIncompleteGroupIdString - Array of error and incomplete groupIdString
 */
const uploadPropertyMenuItemMappingSpreadsheet = async (
  event: React.ChangeEvent<HTMLInputElement>
): Promise<{
  arrArrMenuItemAndMappingToBeCreatedByMenuId: any[];
  arrErrorIncompleteGroupIdString: Array<string>;
}> => {
  const file = event.target.files![0];
  if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    throw new Error('Wrong file type');
  }
  const wb = new ExcelJS.Workbook();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async () => {
      const buffer = reader.result as ExcelJS.Buffer;
      const workbook = await wb.xlsx.load(buffer);
      const arrArrMenuItemAndMappingToBeCreatedByMenuId: any[] = [];
      const arrErrorIncompleteGroupIdString: Array<string> = [];
      let mapClientMenuItemToBeCreatedByMenuItemName = new Map();

      workbook.eachSheet((sheet) => {
        sheet.eachRow((row, rowIndex) => {
          if (rowIndex < 2) return;
          const currentRow: Array<ExcelJS.CellValue> = [];
          let isMerged = false;
          row.eachCell({ includeEmpty: true }, (cell) => {
            if (cell.isMerged) {
              isMerged = true;
              return;
            }
            currentRow.push(cell.value);
          });
          if (isMerged) {
            if (mapClientMenuItemToBeCreatedByMenuItemName.size > 0) {
              arrArrMenuItemAndMappingToBeCreatedByMenuId.push(
                Array.from(mapClientMenuItemToBeCreatedByMenuItemName.values())
              );
              mapClientMenuItemToBeCreatedByMenuItemName = new Map();
            }
            return;
          }
          // groupIdString is 'stringified' in case excel's groupIdString is converted to number by client
          const groupIdString = currentRow[0]!.toString() as string;

          const arrInternalMenuItemId = groupIdString
            .split('/')
            .map((individualGroupIdString) => Number(individualGroupIdString));
          const menuItemName = (currentRow[1] as string) ?? '';
          const station = (currentRow[2] as string) ?? '';
          const costPerKilogram = (currentRow[3] as number) ?? -1;
          const arrWasteId = currentRow.slice(4) as Array<number>;

          const [isMenuItemNameValid, updatedMenuItemName] = checkIsMenuItemNameValid(menuItemName);
          const [isStationValid, updatedStation] = checkIsStationValid(station);
          const isCostPerKilogramValid = checkIsCostPerKilogramValid(costPerKilogram);

          if (isMenuItemNameValid && isStationValid && isCostPerKilogramValid) {
            if (mapClientMenuItemToBeCreatedByMenuItemName.has(updatedMenuItemName)) {
              const clientMenuItemToBeCreated =
                mapClientMenuItemToBeCreatedByMenuItemName.get(updatedMenuItemName);
              mapClientMenuItemToBeCreatedByMenuItemName.set(updatedMenuItemName, {
                ...clientMenuItemToBeCreated,
                arrInternalMenuItemIdWasteId: [
                  ...clientMenuItemToBeCreated.arrInternalMenuItemIdWasteId,
                  ...arrInternalMenuItemId.map((internalMenuItemId) => ({
                    internalMenuItemId,
                    arrWasteId,
                  })),
                ],
              });
            } else {
              mapClientMenuItemToBeCreatedByMenuItemName.set(updatedMenuItemName, {
                menuItemName: updatedMenuItemName,
                station: updatedStation,
                costPerKilogram,
                arrInternalMenuItemIdWasteId: arrInternalMenuItemId.map((internalMenuItemId) => ({
                  internalMenuItemId,
                  arrWasteId,
                })),
              });
            }
          } else {
            arrErrorIncompleteGroupIdString.push(groupIdString);
          }
        });
      });
      if (mapClientMenuItemToBeCreatedByMenuItemName.size > 0) {
        arrArrMenuItemAndMappingToBeCreatedByMenuId.push(
          Array.from(mapClientMenuItemToBeCreatedByMenuItemName.values())
        );
      }
      resolve({ arrArrMenuItemAndMappingToBeCreatedByMenuId, arrErrorIncompleteGroupIdString });
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

export default uploadPropertyMenuItemMappingSpreadsheet;
